// BETA 15.33 — RoomLabScreen.tsx — In-game room/collision/pathfinding tester.
// BETA 15.35 — Major rewrite: pulls EVERYTHING the live game uses.
//
// Per Dylan: "this isn't really right you are missing many thing... see the
// green curve triangles on the inside and outside" + "weird brown barricade
// in a doorway and in the middle of the room. we don't have these" + "please
// just have a png of an actual room under the data so we can see what it
// looks like on actual rooms."
//
// 15.35 fixes:
//   1. Rooms placed at world (5000, 5000) — past EDGE_BUFFER on all sides, so
//      computeBarricades returns []. (The 15.33 RoomLab placed at (0,0) which
//      triggered N+W barricades unconditionally — the bug Dylan flagged.)
//   2. 3-zone collision overlay (RED / BLUE / GREEN) sampled from classifyPoint
//      every 8wu — shows the real cross + trapezoid doorways + corner ellipses
//      + corner clip triangles, NOT the rectangular wall guess from 15.33.
//   3. Doorway TRAPEZOID outlines using actual outer/inner edge constants.
//   4. Quarter-ellipse green corners (4 per room) drawn as filled arcs.
//   5. Corner clip RED triangles (4 per room).
//   6. Exterior green curves (convex / straight / concave / diagonal_bridge)
//      — calls the live `generateExteriorGreenCurves()` and renders each
//      polygon color-coded by type.
//   7. Sample room PNG drawn underneath all geometry (random pick from
//      roomTiles.ts on shape change).
//   8. Dropdown reordered by complexity: solo → 2-room → 3-room → 4-room
//      → larger. Plus weight badge per entry.

import { useEffect, useRef, useState, useMemo } from 'react';
import {
  ROOM_SIZE,
  CLUSTER_SHAPES, type ClusterShape,
  computeGaps, computeWallRects, computeBarricades,
  type WallRect, type GapRect, type Barricade,
} from '../game/roomMapgen';
import {
  classifyPoint,
  generateExteriorGreenCurves,
  type ExteriorGreenCurve,
  WALL, OUTER, CLIP,
  N_OUT_L, N_OUT_R, N_IN_L, N_IN_R,
  S_OUT_L, S_OUT_R, S_IN_L, S_IN_R,
  E_OUT_T, E_OUT_B, E_IN_T, E_IN_B,
  W_OUT_T, W_OUT_B, W_IN_T, W_IN_B,
  CROSS_LEFT, CROSS_RIGHT, CROSS_TOP, CROSS_BOTTOM,
} from '../game/roomCollisionGrid';
import { GRAFFITI_ROOM_TILES } from '../game/roomTiles';

const PLAYER_SIZE = 32;
const ZOOM_DEFAULT = 0.85;

// Per Dylan's barricade bug: rooms placed at world origin (0,0) cause
// computeBarricades to fire on N + W doorways because EDGE_BUFFER = 600.
// Placing at world (5000, 5000) puts the cluster well past the buffer on
// all sides → zero barricades, matching real-game placement.
const LAB_ORIGIN_X = 5000;
const LAB_ORIGIN_Y = 5000;

// Sort dropdown by complexity (room count) — solo first, plus / 2x3 last.
const SORTED_SHAPES = [...CLUSTER_SHAPES].sort((a, b) => {
  if (a.cells.length !== b.cells.length) return a.cells.length - b.cells.length;
  return b.weight - a.weight;
});

interface Props {
  onBack: () => void;
}

interface RoomCell {
  rx: number;
  ry: number;
  walls: WallRect[];
  gaps: GapRect[];
  barricades: Barricade[];
}

function buildRoomsForShape(shape: ClusterShape): RoomCell[] {
  const rooms: RoomCell[] = [];
  for (const [cx, cy] of shape.cells) {
    const rx = LAB_ORIGIN_X + cx * ROOM_SIZE;
    const ry = LAB_ORIGIN_Y + cy * ROOM_SIZE;
    const gaps = computeGaps(rx, ry);
    const walls = computeWallRects(rx, ry);
    const barricades = computeBarricades(rx, ry, gaps);
    rooms.push({ rx, ry, walls, gaps, barricades });
  }
  return rooms;
}

/**
 * Use the live engine's classifyPoint to test if a point is walkable.
 * Mirrors types.ts canMoveTo's 5-corner check.
 */
function checkCollision(x: number, y: number, size: number, rooms: RoomCell[]): {
  walkable: boolean;
  corners: Array<{ x: number; y: number; hits: boolean }>;
} {
  const points = [
    { x: x - size, y: y - size },
    { x: x + size, y: y - size },
    { x: x - size, y: y + size },
    { x: x + size, y: y + size },
    { x: x,        y: y        },
  ];
  const corners = points.map(p => {
    let hits = false;
    let inAnyRoom = false;
    for (const r of rooms) {
      if (p.x >= r.rx && p.x < r.rx + ROOM_SIZE && p.y >= r.ry && p.y < r.ry + ROOM_SIZE) {
        inAnyRoom = true;
        const lx = p.x - r.rx;
        const ly = p.y - r.ry;
        const zone = classifyPoint(lx, ly);
        if (zone === 'red') hits = true;
        // Barricades also block
        for (const b of r.barricades) {
          if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) {
            hits = true; break;
          }
        }
        break;
      }
    }
    if (!inAnyRoom) hits = true; // outside all rooms = blocked
    return { x: p.x, y: p.y, hits };
  });
  const walkable = corners.every(c => !c.hits);
  return { walkable, corners };
}

// ─── BETA 15.37 — Simulation types ────────────────────────────────────
// Per Dylan: "use the exact bot data from cowboy peanut. and have him
// continuously opening up chest and shrines... reset the shrines every
// 30 seconds. give him some weapons. base weapons. easy level 1 enemies."
//
// Cowboy stats used as the bot baseline:
//   - speed: 200 wu/s (player base)
//   - HP: 100 (player base)
//   - weapon: revolver (cowboy's starter; 18 dmg, 1.0s reload, 300wu lock-on)
//   - signature: Quick Draw (+25% dmg + crit on first shot of each burst)
//
// Simplified AI brain (matches cowboy's "balanced" archetype from
// autoPlaytester):
//   - Prioritize unused chest > unused shrine > nearest enemy in range
//   - Flee if HP < 25% AND enemy within 80wu
//   - Wander toward random doorway every 5s if nothing else to do

interface SimEntity {
  id: number;
  type: 'bot' | 'zombie';   // 23.55 — timmy deleted (23.39 took caseoh)
  // BETA 15.63 — jump arc lerp endpoints (mirrors enemySystem.ts)
  jumpStartX?: number;
  jumpStartY?: number;
  jumpTargetX?: number;
  jumpTargetY?: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  maxHp: number;
  size: number;          // hitbox radius
  speed: number;         // wu/s
  damage: number;        // contact damage
  fireCooldown: number;  // s until next shot (bot only)
  lastMoveCheck: { x: number; y: number; t: number }; // for stuck detection
  stuckTimer: number;    // s spent not moving
  goalX: number;         // current goal position
  goalY: number;
  goalRefreshAt: number; // sim time to pick a new goal
  // BETA 15.59 — wall-hop teleport jump (mirrors real game enemySystem.ts)
  /** Accumulates dt while enemy isn't progressing toward the bot/player.
   *  Computed via dot product of movement vector × player-direction. */
  wallPressTimer: number;
  /** When > 0, sprite-grow visual is playing (post-jump landing).
   *  Mirrors real game's `phaseTimer`. 0.4s duration. */
  phaseTimer: number;
  phaseMaxTimer: number;
  /** Track total wall-hops so the lab UI can display a counter. */
  wallHopCount: number;
}

interface SimShrine {
  id: number;
  x: number;
  y: number;
  used: boolean;
  respawnAt: number;     // sim time when this becomes unused again
}

interface SimChest {
  id: number;
  x: number;
  y: number;
  opened: boolean;
  respawnAt: number;
}

interface SimBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
  isCrit: boolean;
  lifetime: number;     // s
  age: number;
}

interface StuckPoint {
  x: number;
  y: number;
  type: 'bot' | 'zombie';   // 23.55 — timmy deleted (23.39 took caseoh)
  spawnTime: number;    // sim time when registered (for fade-out)
}

interface SimState {
  running: boolean;
  speed: number;        // 0.5, 1.0, 2.0
  simTime: number;      // simulated seconds since sim start
  bot: SimEntity | null;
  enemies: SimEntity[];
  shrines: SimShrine[];
  chests: SimChest[];
  bullets: SimBullet[];
  stuckPoints: StuckPoint[];
  nextEnemyId: number;
  nextShrineId: number;
  nextChestId: number;
  // Stats
  totalKills: number;
  totalShrinesUsed: number;
  totalChestsOpened: number;
  totalDeaths: number;
}

const SHRINE_RESPAWN_SEC = 30;  // Per Dylan: "reset the shrines every 30 seconds"
const CHEST_RESPAWN_SEC = 30;
const BOT_INTERACT_RANGE = 50;  // wu — distance at which bot "uses" shrine/chest
const ENEMY_SPAWN_INTERVAL = 5; // s — auto-spawn one zombie outside layout
const REVOLVER_DAMAGE = 18;
const REVOLVER_FIRE_RATE = 1.0; // s between shots
const REVOLVER_RANGE = 300;
const REVOLVER_CRIT_CHANCE = 0.06;
const REVOLVER_CRIT_DAMAGE = 0.4;

/** Build initial sim entities from current room layout */
function buildInitialSimState(rooms: RoomCell[]): SimState {
  const cx0 = rooms[0].rx + ROOM_SIZE / 2;
  const cy0 = rooms[0].ry + ROOM_SIZE / 2;

  // Place 1 shrine + 1 chest in EACH room, near opposite corners
  const shrines: SimShrine[] = [];
  const chests: SimChest[] = [];
  let sId = 0, cId = 0;
  for (const r of rooms) {
    shrines.push({
      id: sId++,
      x: r.rx + ROOM_SIZE * 0.3,
      y: r.ry + ROOM_SIZE * 0.3,
      used: false,
      respawnAt: 0,
    });
    chests.push({
      id: cId++,
      x: r.rx + ROOM_SIZE * 0.7,
      y: r.ry + ROOM_SIZE * 0.7,
      opened: false,
      respawnAt: 0,
    });
  }

  return {
    running: false,
    speed: 1.0,
    simTime: 0,
    bot: {
      id: 0,
      type: 'bot',
      x: cx0, y: cy0,
      vx: 0, vy: 0,
      hp: 100, maxHp: 100,
      size: 32,
      speed: 200,
      damage: 0,
      fireCooldown: 0,
      lastMoveCheck: { x: cx0, y: cy0, t: 0 },
      stuckTimer: 0,
      goalX: cx0, goalY: cy0,
      goalRefreshAt: 0,
      wallPressTimer: 0,
      phaseTimer: 0,
      phaseMaxTimer: 0,
      wallHopCount: 0,
    },
    enemies: [],
    shrines,
    chests,
    bullets: [],
    stuckPoints: [],
    nextEnemyId: 1,
    nextShrineId: sId,
    nextChestId: cId,
    totalKills: 0,
    totalShrinesUsed: 0,
    totalChestsOpened: 0,
    totalDeaths: 0,
  };
}

/** Fast lab-only collision: matches RoomLab's `checkCollision` but returns just walkable */
function isWalkableInRooms(x: number, y: number, size: number, rooms: RoomCell[], outerBounds: { minX: number; minY: number; maxX: number; maxY: number }): boolean {
  // Outer collision boundary — keeps entities inside the layout
  if (x - size < outerBounds.minX || x + size > outerBounds.maxX ||
      y - size < outerBounds.minY || y + size > outerBounds.maxY) return false;
  // 5-corner check inside any room
  const points = [
    [x - size, y - size], [x + size, y - size],
    [x - size, y + size], [x + size, y + size],
    [x, y],
  ];
  for (const [px, py] of points) {
    let inside = false;
    for (const r of rooms) {
      if (px >= r.rx && px < r.rx + ROOM_SIZE && py >= r.ry && py < r.ry + ROOM_SIZE) {
        inside = true;
        const lx = px - r.rx;
        const ly = py - r.ry;
        if (classifyPoint(lx, ly) === 'red') return false;
        break;
      }
    }
    if (!inside) return false;
  }
  return true;
}

/** Try to move entity by (dx, dy), sliding along walls. Returns final position. */
function moveEntity(
  e: SimEntity, dx: number, dy: number, rooms: RoomCell[],
  outerBounds: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  const tryX = e.x + dx;
  const tryY = e.y + dy;
  if (isWalkableInRooms(tryX, tryY, e.size, rooms, outerBounds)) {
    e.x = tryX; e.y = tryY;
    return true;
  }
  // Try sliding along each axis
  if (isWalkableInRooms(e.x + dx, e.y, e.size, rooms, outerBounds)) {
    e.x += dx;
    return true;
  }
  if (isWalkableInRooms(e.x, e.y + dy, e.size, rooms, outerBounds)) {
    e.y += dy;
    return true;
  }
  return false;
}

/** Spawn a level-1 zombie at a random doorway edge, walking into the layout */
function spawnZombieAtEdge(sim: SimState, rooms: RoomCell[], outerBounds: { minX: number; minY: number; maxX: number; maxY: number }): void {
  // Pick a random room's random doorway (gap)
  const room = rooms[Math.floor(Math.random() * rooms.length)];
  const gap = room.gaps[Math.floor(Math.random() * room.gaps.length)];
  // Spawn just inside the doorway opening
  const sx = gap.x + gap.w / 2;
  const sy = gap.y + gap.h / 2;
  // Verify walkable
  if (!isWalkableInRooms(sx, sy, 22, rooms, outerBounds)) return;
  sim.enemies.push({
    id: sim.nextEnemyId++,
    type: 'zombie',
    x: sx, y: sy,
    vx: 0, vy: 0,
    hp: 50, maxHp: 50,
    size: 22,
    speed: 80,
    damage: 25,
    fireCooldown: 0,
    lastMoveCheck: { x: sx, y: sy, t: sim.simTime },
    stuckTimer: 0,
    goalX: 0, goalY: 0,
    goalRefreshAt: 0,
    wallPressTimer: 0,
    phaseTimer: 0,
    phaseMaxTimer: 0,
    wallHopCount: 0,
  });
}

// 23.55 — spawnTimmyAtEdge deleted with Timmy

/** Per-frame sim tick — moves bot, enemies, fires bullets, handles pickups */
function runSimTick(
  sim: SimState, dt: number,
  rooms: RoomCell[],
  outerBounds: { minX: number; minY: number; maxX: number; maxY: number },
): void {
  if (!sim.bot || sim.bot.hp <= 0) {
    if (sim.bot && sim.bot.hp <= 0) sim.totalDeaths += 1;
    return;
  }
  const bot = sim.bot;

  // === Bot AI: cowboy-style decision ===
  // Priority: flee if HP < 25% AND enemy near, else nearest unused chest, else nearest unused shrine, else wander.
  const lowHp = bot.hp < bot.maxHp * 0.25;
  let nearestEnemy: SimEntity | null = null;
  let nearestEnemyDist = Infinity;
  for (const e of sim.enemies) {
    if (e.hp <= 0) continue;
    const d = Math.hypot(e.x - bot.x, e.y - bot.y);
    if (d < nearestEnemyDist) { nearestEnemyDist = d; nearestEnemy = e; }
  }

  let goalX = bot.goalX, goalY = bot.goalY;
  if (lowHp && nearestEnemy && nearestEnemyDist < 200) {
    // Flee: move AWAY from nearest enemy
    const dx = bot.x - nearestEnemy.x;
    const dy = bot.y - nearestEnemy.y;
    const d = Math.hypot(dx, dy) || 1;
    goalX = bot.x + (dx / d) * 200;
    goalY = bot.y + (dy / d) * 200;
  } else {
    // Find nearest unused chest
    let nearestChest: SimChest | null = null;
    let nearestChestDist = Infinity;
    for (const c of sim.chests) {
      if (c.opened) continue;
      const d = Math.hypot(c.x - bot.x, c.y - bot.y);
      if (d < nearestChestDist) { nearestChestDist = d; nearestChest = c; }
    }
    let nearestShrine: SimShrine | null = null;
    let nearestShrineDist = Infinity;
    for (const s of sim.shrines) {
      if (s.used) continue;
      const d = Math.hypot(s.x - bot.x, s.y - bot.y);
      if (d < nearestShrineDist) { nearestShrineDist = d; nearestShrine = s; }
    }
    if (nearestChest && nearestChestDist <= nearestShrineDist) {
      goalX = nearestChest.x; goalY = nearestChest.y;
    } else if (nearestShrine) {
      goalX = nearestShrine.x; goalY = nearestShrine.y;
    } else if (sim.simTime > bot.goalRefreshAt) {
      // No goals — wander to a random doorway every 5s
      const room = rooms[Math.floor(Math.random() * rooms.length)];
      const gap = room.gaps[Math.floor(Math.random() * room.gaps.length)];
      goalX = gap.x + gap.w / 2;
      goalY = gap.y + gap.h / 2;
      bot.goalRefreshAt = sim.simTime + 5;
    }
  }
  bot.goalX = goalX;
  bot.goalY = goalY;

  // === Bot movement ===
  {
    const dx = goalX - bot.x;
    const dy = goalY - bot.y;
    const d = Math.hypot(dx, dy);
    if (d > 4) {
      const stepX = (dx / d) * bot.speed * dt;
      const stepY = (dy / d) * bot.speed * dt;
      moveEntity(bot, stepX, stepY, rooms, outerBounds);
    }
  }

  // === Bot fires revolver at nearest enemy in range ===
  bot.fireCooldown = Math.max(0, bot.fireCooldown - dt);
  if (bot.fireCooldown <= 0 && nearestEnemy && nearestEnemyDist <= REVOLVER_RANGE) {
    bot.fireCooldown = REVOLVER_FIRE_RATE;
    const dx = nearestEnemy.x - bot.x;
    const dy = nearestEnemy.y - bot.y;
    const d = Math.hypot(dx, dy) || 1;
    const isCrit = Math.random() < REVOLVER_CRIT_CHANCE;
    const dmg = isCrit ? Math.round(REVOLVER_DAMAGE * (1 + REVOLVER_CRIT_DAMAGE)) : REVOLVER_DAMAGE;
    sim.bullets.push({
      x: bot.x, y: bot.y,
      vx: (dx / d) * 1400, vy: (dy / d) * 1400,
      damage: dmg,
      isCrit,
      lifetime: 1.0,
      age: 0,
    });
  }

  // === Bot pickup interactions (use shrine / open chest) ===
  for (const s of sim.shrines) {
    if (s.used) {
      if (sim.simTime >= s.respawnAt) s.used = false;
      continue;
    }
    if (Math.hypot(s.x - bot.x, s.y - bot.y) < BOT_INTERACT_RANGE) {
      s.used = true;
      s.respawnAt = sim.simTime + SHRINE_RESPAWN_SEC;
      sim.totalShrinesUsed++;
      // Heal 25 HP per shrine (simulated buff effect)
      bot.hp = Math.min(bot.maxHp, bot.hp + 25);
    }
  }
  for (const c of sim.chests) {
    if (c.opened) {
      if (sim.simTime >= c.respawnAt) c.opened = false;
      continue;
    }
    if (Math.hypot(c.x - bot.x, c.y - bot.y) < BOT_INTERACT_RANGE) {
      c.opened = true;
      c.respawnAt = sim.simTime + CHEST_RESPAWN_SEC;
      sim.totalChestsOpened++;
    }
  }

  // === Enemies: chase bot + WALL-HOP TELEPORT JUMP + contact damage ===
  // BETA 15.59 — RoomLab simulation mirrors the real game's wall-hop logic
  // (enemySystem.ts dot-product trigger + wall-crossing scan + climb-scale
  // visual). Lets Dylan visually verify jumps in the lab without running
  // a full bot session. Per Dylan: "you have room labs built. add jumping
  // simulation. use real fucxking sprites. not circled."
  for (const e of sim.enemies) {
    if (e.hp <= 0) continue;

    // Tick down phase (sprite-grow visual after a jump)
    if (e.phaseTimer > 0) e.phaseTimer = Math.max(0, e.phaseTimer - dt);

    // BETA 15.63 — JUMP ARC LERP. While phasing, lerp position from
    // jumpStart → jumpTarget instead of teleporting. Sprite visibly
    // travels along the arc (combined with the climbScale + Y-lift in
    // render).
    if (e.phaseTimer > 0 && e.phaseMaxTimer > 0 && e.jumpStartX !== undefined
        && e.jumpStartY !== undefined && e.jumpTargetX !== undefined && e.jumpTargetY !== undefined) {
      const jumpProgress = 1 - e.phaseTimer / e.phaseMaxTimer;
      const eased = jumpProgress * jumpProgress * (3 - 2 * jumpProgress);
      e.x = e.jumpStartX + (e.jumpTargetX - e.jumpStartX) * eased;
      e.y = e.jumpStartY + (e.jumpTargetY - e.jumpStartY) * eased;
      // Skip normal movement / wall-hop check during the jump arc
      // Contact damage check still runs below (as in real game)
      const finalD = Math.hypot(bot.x - e.x, bot.y - e.y) || 1;
      if (finalD < bot.size + e.size && bot.hp > 0) {
        bot.hp = Math.max(0, bot.hp - e.damage * dt);
      }
      continue;
    }

    const dx = bot.x - e.x;
    const dy = bot.y - e.y;
    const d = Math.hypot(dx, dy) || 1;

    // Capture old position for the dot-product trigger
    const oldX = e.x;
    const oldY = e.y;

    const stepX = (dx / d) * e.speed * dt;
    const stepY = (dy / d) * e.speed * dt;
    moveEntity(e, stepX, stepY, rooms, outerBounds);

    // ── BETA 15.61 — WALL-HOP TRIGGER (mirrors enemySystem.ts) ──
    // Smooth wall-touch (no slide, no jitter) + 1s touch timer → jump.
    if (e.phaseTimer <= 0) {
      // Soft cap: revert if frame's movement clipped > 2% into a wall
      const SOFT_LIMIT = e.size * 1.02;
      if (!isWalkableInRooms(e.x, e.y, SOFT_LIMIT, rooms, outerBounds)) {
        e.x = oldX; e.y = oldY;
      }
      // Touch detection at 1.10× radius
      const TOUCH_PROBE = e.size * 1.10;
      const touchingWall = !isWalkableInRooms(e.x, e.y, TOUCH_PROBE, rooms, outerBounds);
      if (touchingWall) {
        e.wallPressTimer += dt;
      } else {
        e.wallPressTimer = 0;
      }

      // ── BETA 15.63 — JUMP ARC (mirrors enemySystem.ts) ──
      // Save start + target instead of teleporting. Position lerps over
      // the phase animation (handled at top of enemy update loop).
      if (e.wallPressTimer >= 1.0 && d > 1) {
        const dirX = dx / d;
        const dirY = dy / d;
        const SCAN_STEP = 8;
        const SCAN_MAX = 350;
        let landed = false;
        let foundWall = false;
        let targetX = e.x;
        let targetY = e.y;
        for (let dist = SCAN_STEP; dist <= SCAN_MAX; dist += SCAN_STEP) {
          const tx = e.x + dirX * dist;
          const ty = e.y + dirY * dist;
          const blocked = !isWalkableInRooms(tx, ty, e.size, rooms, outerBounds);
          if (blocked) {
            foundWall = true;
          } else if (foundWall) {
            targetX = tx; targetY = ty;
            landed = true;
            break;
          }
        }
        if (!landed && !foundWall) {
          const tx = e.x + dirX * 60;
          const ty = e.y + dirY * 60;
          if (isWalkableInRooms(tx, ty, e.size, rooms, outerBounds)) {
            targetX = tx; targetY = ty;
            landed = true;
          }
        }
        if (landed) {
          // BETA 15.63 — save lerp endpoints, don't teleport
          e.jumpStartX = e.x;
          e.jumpStartY = e.y;
          e.jumpTargetX = targetX;
          e.jumpTargetY = targetY;
          e.phaseTimer = 0.7;
          e.phaseMaxTimer = 0.7;
          e.wallPressTimer = 0;
          e.wallHopCount++;
        } else {
          e.wallPressTimer = Math.max(0, e.wallPressTimer - 0.5);
        }
      }
    }

    // Contact damage (use updated d after potential teleport)
    const finalD = Math.hypot(bot.x - e.x, bot.y - e.y) || 1;
    if (finalD < bot.size + e.size && bot.hp > 0) {
      bot.hp = Math.max(0, bot.hp - e.damage * dt);
    }
  }

  // === Bullet movement + enemy hit detection ===
  for (let i = sim.bullets.length - 1; i >= 0; i--) {
    const b = sim.bullets[i];
    b.age += dt;
    b.x += b.vx * dt;
    b.y += b.vy * dt;
    if (b.age > b.lifetime) {
      sim.bullets.splice(i, 1);
      continue;
    }
    for (const e of sim.enemies) {
      if (e.hp <= 0) continue;
      const d = Math.hypot(e.x - b.x, e.y - b.y);
      if (d < e.size + 4) {
        e.hp -= b.damage;
        if (e.hp <= 0) sim.totalKills++;
        sim.bullets.splice(i, 1);
        break;
      }
    }
  }

  // Cleanup dead enemies
  sim.enemies = sim.enemies.filter(e => e.hp > 0);

  // === Stuck detection — every 0.5s of sim time, check if entity moved ===
  const STUCK_CHECK_INTERVAL = 0.5;
  for (const e of [bot, ...sim.enemies]) {
    if (e.hp <= 0) continue;
    if (sim.simTime - e.lastMoveCheck.t >= STUCK_CHECK_INTERVAL) {
      const moved = Math.hypot(e.x - e.lastMoveCheck.x, e.y - e.lastMoveCheck.y);
      if (moved < 5) {
        e.stuckTimer += STUCK_CHECK_INTERVAL;
        if (e.stuckTimer >= 0.5) {
          // Register a stuck event (heatmap dot)
          sim.stuckPoints.push({ x: e.x, y: e.y, type: e.type, spawnTime: sim.simTime });
          e.stuckTimer = 0;
        }
      } else {
        e.stuckTimer = 0;
      }
      e.lastMoveCheck = { x: e.x, y: e.y, t: sim.simTime };
    }
  }

  // Fade out old stuck points (keep last 60s)
  sim.stuckPoints = sim.stuckPoints.filter(p => sim.simTime - p.spawnTime < 60);
}

export default function RoomLabScreen({ onBack }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [shapeName, setShapeName] = useState<string>('solo');
  const [showZoneOverlay, setShowZoneOverlay] = useState(true);
  const [showExteriorCurves, setShowExteriorCurves] = useState(true);
  const [showRoomPng, setShowRoomPng] = useState(true);
  const [showCorners, setShowCorners] = useState(true);
  const [showDoorwayTrap, setShowDoorwayTrap] = useState(false);
  // Player position — defaults to first room's center (in world units)
  const [playerWorld, setPlayerWorld] = useState<{ x: number; y: number }>(
    { x: LAB_ORIGIN_X + ROOM_SIZE / 2, y: LAB_ORIGIN_Y + ROOM_SIZE / 2 }
  );
  const [hoverWorld, setHoverWorld] = useState<{ x: number; y: number } | null>(null);

  // Pick a random room PNG when the shape changes (or on initial render).
  const [roomPngUrls, setRoomPngUrls] = useState<string[]>(() => {
    const tiles = GRAFFITI_ROOM_TILES.length > 0 ? GRAFFITI_ROOM_TILES : [];
    return tiles.length > 0 ? [tiles[Math.floor(Math.random() * tiles.length)].cdnUrl] : [];
  });

  const shape = useMemo(() => SORTED_SHAPES.find(s => s.name === shapeName) ?? SORTED_SHAPES[0], [shapeName]);
  const rooms = useMemo(() => buildRoomsForShape(shape), [shape]);

  // Pick N random PNGs when shape changes (one per room)
  useEffect(() => {
    if (GRAFFITI_ROOM_TILES.length === 0) return;
    const next: string[] = [];
    for (let i = 0; i < shape.cells.length; i++) {
      const t = GRAFFITI_ROOM_TILES[Math.floor(Math.random() * GRAFFITI_ROOM_TILES.length)];
      next.push(t.cdnUrl);
    }
    setRoomPngUrls(next);
    // Re-center player on first room of new shape
    const first = shape.cells[0];
    setPlayerWorld({
      x: LAB_ORIGIN_X + first[0] * ROOM_SIZE + ROOM_SIZE / 2,
      y: LAB_ORIGIN_Y + first[1] * ROOM_SIZE + ROOM_SIZE / 2,
    });
  }, [shape]);

  // Layout bounds (with margin for exterior curves which extend ~300wu beyond rooms)
  const layoutBounds = useMemo(() => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of rooms) {
      minX = Math.min(minX, r.rx);
      minY = Math.min(minY, r.ry);
      maxX = Math.max(maxX, r.rx + ROOM_SIZE);
      maxY = Math.max(maxY, r.ry + ROOM_SIZE);
    }
    // Pad 400wu to fit exterior curves
    return { minX: minX - 400, minY: minY - 400, maxX: maxX + 400, maxY: maxY + 400 };
  }, [rooms]);

  const layoutW = layoutBounds.maxX - layoutBounds.minX;
  const layoutH = layoutBounds.maxY - layoutBounds.minY;

  // Pre-compute exterior green curves (calls the live engine fn)
  const exteriorCurves = useMemo<ExteriorGreenCurve[]>(() => {
    const roomsForCurves = rooms.map(r => ({ x: r.rx, y: r.ry, size: ROOM_SIZE }));
    return generateExteriorGreenCurves(roomsForCurves, ROOM_SIZE);
  }, [rooms]);

  // Image cache for room PNGs
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());
  useEffect(() => {
    for (const url of roomPngUrls) {
      if (!imageCache.current.has(url)) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
        imageCache.current.set(url, img);
        img.onload = () => {
          // Trigger re-render once loaded
          setRoomPngUrls(prev => [...prev]);
        };
      }
    }
  }, [roomPngUrls]);

  // === BETA 15.37 — Simulation state ===
  // Sim state lives in a ref so per-frame ticks don't trigger React re-renders.
  // A separate setSimFrame counter increments every ~6 sim ticks to drive UI
  // re-renders (HP bar, stats panel) at ~10fps without melting React.
  const simStateRef = useRef<SimState>(buildInitialSimState(rooms));
  const [simFrame, setSimFrame] = useState(0);
  const [simRunning, setSimRunning] = useState(false);
  const [simSpeed, setSimSpeed] = useState(1.0);
  const [showStuckHeatmap, setShowStuckHeatmap] = useState(true);
  const lastTickWallTimeRef = useRef<number>(0);
  const enemySpawnTimerRef = useRef<number>(0);

  // Reset sim when shape changes
  useEffect(() => {
    simStateRef.current = buildInitialSimState(rooms);
    enemySpawnTimerRef.current = 0;
    setSimRunning(false);
    setSimFrame(f => f + 1);
  }, [rooms]);

  // === Sim tick loop (rAF-driven) ===
  useEffect(() => {
    if (!simRunning) return;
    let rafId = 0;
    let frameCounter = 0;

    const tick = () => {
      const now = performance.now();
      const sim = simStateRef.current;
      // Real wall-time delta capped at 50ms (don't let tab-throttling lurch the sim)
      const dtRaw = lastTickWallTimeRef.current === 0
        ? 1 / 60
        : Math.min(0.05, (now - lastTickWallTimeRef.current) / 1000);
      lastTickWallTimeRef.current = now;
      const dt = dtRaw * sim.speed;
      sim.simTime += dt;

      runSimTick(sim, dt, rooms, layoutBounds);

      // Auto-spawn 1 zombie every ENEMY_SPAWN_INTERVAL seconds
      enemySpawnTimerRef.current += dt;
      if (enemySpawnTimerRef.current >= ENEMY_SPAWN_INTERVAL && sim.enemies.length < 30) {
        enemySpawnTimerRef.current = 0;
        spawnZombieAtEdge(sim, rooms, layoutBounds);
      }

      // React re-render every ~6 frames for stats / HP UI updates
      frameCounter++;
      if (frameCounter >= 6) {
        frameCounter = 0;
        setSimFrame(f => f + 1);
      }
      rafId = requestAnimationFrame(tick);
    };

    lastTickWallTimeRef.current = 0;
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [simRunning, rooms, layoutBounds]);

  // Sim controls
  const handleStartStop = () => {
    if (simStateRef.current.bot && simStateRef.current.bot.hp <= 0) {
      // Reset on death before resuming
      simStateRef.current = buildInitialSimState(rooms);
      enemySpawnTimerRef.current = 0;
    }
    simStateRef.current.running = !simRunning;
    setSimRunning(r => !r);
  };
  const handleResetSim = () => {
    simStateRef.current = buildInitialSimState(rooms);
    enemySpawnTimerRef.current = 0;
    setSimRunning(false);
    setSimFrame(f => f + 1);
  };
  const handleSpawnZombie = () => {
    spawnZombieAtEdge(simStateRef.current, rooms, layoutBounds);
    setSimFrame(f => f + 1);
  };
  const handleClearEnemies = () => {
    simStateRef.current.enemies = [];
    simStateRef.current.bullets = [];
    setSimFrame(f => f + 1);
  };
  const handleChangeSpeed = (s: number) => {
    simStateRef.current.speed = s;
    setSimSpeed(s);
  };

  // Small helper for sim control button styling
  const btnStyle = (accent: string): React.CSSProperties => ({
    padding: '4px 8px',
    fontSize: 10,
    fontFamily: 'inherit',
    background: 'rgba(20,28,36,0.85)',
    color: accent,
    border: `1px solid ${accent}`,
    borderRadius: 2,
    cursor: 'pointer',
  });

  // === Render ===
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const zoomX = (W - 80) / layoutW;
    const zoomY = (H - 80) / layoutH;
    const zoom = Math.min(zoomX, zoomY) * ZOOM_DEFAULT;
    const offsetX = (W - layoutW * zoom) / 2 - layoutBounds.minX * zoom;
    const offsetY = (H - layoutH * zoom) / 2 - layoutBounds.minY * zoom;

    const w2c = (wx: number, wy: number): [number, number] => [
      offsetX + wx * zoom,
      offsetY + wy * zoom,
    ];

    // Background
    ctx.fillStyle = '#0a0c10';
    ctx.fillRect(0, 0, W, H);

    // === LAYER 1: Outside texture (just a darker fill outside rooms) ===
    // The real game has a forest tile here; we just dim the area for clarity.
    // This makes the room PNGs pop visually.

    // === LAYER 2: Room PNGs ===
    if (showRoomPng) {
      for (let i = 0; i < rooms.length; i++) {
        const r = rooms[i];
        const url = roomPngUrls[i] ?? roomPngUrls[0];
        const img = url ? imageCache.current.get(url) : null;
        const [x, y] = w2c(r.rx, r.ry);
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, x, y, ROOM_SIZE * zoom, ROOM_SIZE * zoom);
        } else {
          // Fallback: dark floor
          ctx.fillStyle = '#28332e';
          ctx.fillRect(x, y, ROOM_SIZE * zoom, ROOM_SIZE * zoom);
        }
      }
    } else {
      // Plain floor when PNG hidden
      ctx.fillStyle = '#28332e';
      for (const r of rooms) {
        const [x, y] = w2c(r.rx, r.ry);
        ctx.fillRect(x, y, ROOM_SIZE * zoom, ROOM_SIZE * zoom);
      }
    }

    // === LAYER 3: 3-zone collision overlay (sample classifyPoint per cell) ===
    if (showZoneOverlay) {
      const STEP = 8; // sample every 8wu — good resolution without melting the CPU
      for (const r of rooms) {
        for (let ly = 0; ly < ROOM_SIZE; ly += STEP) {
          for (let lx = 0; lx < ROOM_SIZE; lx += STEP) {
            const zone = classifyPoint(lx, ly);
            const [x, y] = w2c(r.rx + lx, r.ry + ly);
            if (zone === 'red') {
              ctx.fillStyle = 'rgba(255,40,40,0.35)';
              ctx.fillRect(x, y, STEP * zoom + 1, STEP * zoom + 1);
            } else if (zone === 'blue') {
              ctx.fillStyle = 'rgba(40,120,255,0.22)';
              ctx.fillRect(x, y, STEP * zoom + 1, STEP * zoom + 1);
            } else if (zone === 'green') {
              ctx.fillStyle = 'rgba(40,255,40,0.40)';
              ctx.fillRect(x, y, STEP * zoom + 1, STEP * zoom + 1);
            }
          }
        }
      }
    }

    // === LAYER 4: Doorway trapezoid outlines ===
    if (showDoorwayTrap) {
      ctx.strokeStyle = 'rgba(0,255,255,0.85)';
      ctx.lineWidth = 1.5;
      for (const r of rooms) {
        // North trapezoid
        let pts = [
          [r.rx + N_OUT_L, r.ry],
          [r.rx + N_OUT_R, r.ry],
          [r.rx + N_IN_R,  r.ry + WALL],
          [r.rx + N_IN_L,  r.ry + WALL],
        ];
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const [px, py] = w2c(pts[i][0], pts[i][1]);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
        // South
        pts = [
          [r.rx + S_OUT_L, r.ry + ROOM_SIZE],
          [r.rx + S_OUT_R, r.ry + ROOM_SIZE],
          [r.rx + S_IN_R,  r.ry + OUTER],
          [r.rx + S_IN_L,  r.ry + OUTER],
        ];
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const [px, py] = w2c(pts[i][0], pts[i][1]);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
        // West
        pts = [
          [r.rx,             r.ry + W_OUT_T],
          [r.rx,             r.ry + W_OUT_B],
          [r.rx + WALL,      r.ry + W_IN_B],
          [r.rx + WALL,      r.ry + W_IN_T],
        ];
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const [px, py] = w2c(pts[i][0], pts[i][1]);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
        // East
        pts = [
          [r.rx + ROOM_SIZE, r.ry + E_OUT_T],
          [r.rx + ROOM_SIZE, r.ry + E_OUT_B],
          [r.rx + OUTER,     r.ry + E_IN_B],
          [r.rx + OUTER,     r.ry + E_IN_T],
        ];
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const [px, py] = w2c(pts[i][0], pts[i][1]);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }

    // === LAYER 5: Corner clip triangles — REMOVED IN BETA 15.40 ===
    // Per Dylan: only walls + doorways + floor remain as room collision.
    // The 48wu right-triangles at each inner wall corner are gone from
    // classifyPoint, so this overlay is gone too. Toggle still exists in
    // the sidebar but is now a no-op for backward compatibility.

    // === LAYER 6: Exterior green curves (the polygons OUTSIDE the rooms) ===
    if (showExteriorCurves) {
      for (const curve of exteriorCurves) {
        if (curve.polygon.length < 3) continue;
        // Color-code by type
        let fill = 'rgba(40,255,40,0.35)';
        let stroke = 'rgba(40,220,40,0.8)';
        if (curve.type === 'diagonal_bridge') {
          fill = 'rgba(0,200,255,0.32)';
          stroke = 'rgba(0,200,255,0.8)';
        } else if (curve.type === 'concave') {
          fill = 'rgba(255,200,40,0.32)';
          stroke = 'rgba(255,200,40,0.8)';
        } else if (curve.type === 'straight') {
          fill = 'rgba(140,255,40,0.28)';
          stroke = 'rgba(140,255,40,0.8)';
        }
        ctx.fillStyle = fill;
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        for (let i = 0; i < curve.polygon.length; i++) {
          const [px, py] = w2c(curve.polygon[i].x, curve.polygon[i].y);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    }

    // === LAYER 7: Barricades (only visible if the layout is at world edge — won't fire here) ===
    ctx.fillStyle = 'rgba(120,80,40,0.85)';
    ctx.strokeStyle = '#a87842';
    for (const r of rooms) {
      for (const b of r.barricades) {
        const [bx, by] = w2c(b.x, b.y);
        ctx.fillRect(bx, by, b.w * zoom, b.h * zoom);
        ctx.strokeRect(bx, by, b.w * zoom, b.h * zoom);
      }
    }

    // === LAYER 7.5 — BETA 15.37 — Simulation entities + shrines + chests + bullets + heatmap ===
    {
      const sim = simStateRef.current;

      // Stuck heatmap (drawn under entities so dots are visible behind them)
      if (showStuckHeatmap) {
        for (const sp of sim.stuckPoints) {
          const age = sim.simTime - sp.spawnTime;
          const alpha = Math.max(0.15, 0.55 - age / 60);  // fades over 60s
          const [px, py] = w2c(sp.x, sp.y);
          ctx.fillStyle = sp.type === 'bot' ? `rgba(255,80,80,${alpha})` : `rgba(255,180,40,${alpha})`;
          ctx.beginPath();
          ctx.arc(px, py, 5 * zoom + 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Shrines (blue glowing circles when available, dim when used)
      for (const s of sim.shrines) {
        const [px, py] = w2c(s.x, s.y);
        const r = 18 * zoom;
        if (s.used) {
          ctx.fillStyle = 'rgba(80,120,180,0.25)';
          ctx.strokeStyle = 'rgba(80,120,180,0.4)';
        } else {
          ctx.fillStyle = 'rgba(40,200,255,0.45)';
          ctx.strokeStyle = '#80f0ff';
        }
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Center symbol
        ctx.fillStyle = s.used ? 'rgba(255,255,255,0.4)' : '#ffffff';
        ctx.font = `bold ${Math.round(14 * zoom)}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('S', px, py);
      }

      // Chests (gold squares when unopened, dim when opened)
      for (const c of sim.chests) {
        const [px, py] = w2c(c.x, c.y);
        const r = 18 * zoom;
        if (c.opened) {
          ctx.fillStyle = 'rgba(140,110,40,0.25)';
          ctx.strokeStyle = 'rgba(140,110,40,0.4)';
        } else {
          ctx.fillStyle = 'rgba(255,200,40,0.5)';
          ctx.strokeStyle = '#ffd040';
        }
        ctx.lineWidth = 2;
        ctx.fillRect(px - r, py - r, r * 2, r * 2);
        ctx.strokeRect(px - r, py - r, r * 2, r * 2);
        ctx.fillStyle = c.opened ? 'rgba(255,255,255,0.4)' : '#ffffff';
        ctx.font = `bold ${Math.round(14 * zoom)}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('C', px, py);
      }

      // Enemies — BETA 15.59 REAL SPRITES (zombie/timmy) + climb-scale + JUMP text
      // Per Dylan: "use real fucxking sprites. not circled."
      for (const e of sim.enemies) {
        if (e.hp <= 0) continue;
        const [px, py] = w2c(e.x, e.y);
        const r = e.size * zoom;
        // ── BETA 15.63 — climb-scale + render-side Y-lift for jump arc ──
        const isPhasing = e.phaseTimer > 0 && e.phaseMaxTimer > 0;
        const phaseProgress = isPhasing ? 1 - e.phaseTimer / e.phaseMaxTimer : 0;
        const climbScale = isPhasing ? 1 + Math.sin(phaseProgress * Math.PI) * 1.2 : 1; // BETA 15.63: 0.6 → 1.2 (peak 2.2×)
        const arcLiftY = isPhasing ? -Math.sin(phaseProgress * Math.PI) * e.size * 0.5 * zoom : 0;
        const drawSize = r * 2 * climbScale;
        const spritePy = py + arcLiftY;
        // ── Resolve real game sprite ──
        const spriteUrl = '/cdn/viewbotter_walk_right_c1f7adc7.png';   // 23.55 — zombie is the only sim enemy
        const enemySprite = imageCache.current.get(spriteUrl);
        if (enemySprite && enemySprite.complete && enemySprite.naturalWidth > 0) {
          const aspect = enemySprite.naturalWidth / enemySprite.naturalHeight;
          const sw = drawSize * aspect;
          const sh = drawSize;
          ctx.drawImage(enemySprite, px - sw / 2, spritePy - sh / 2, sw, sh);
        } else {
          // Fallback while sprite loads — colored disc
          ctx.fillStyle = e.type === 'zombie' ? 'rgba(180,40,40,0.85)' : 'rgba(255,140,40,0.85)';
          ctx.beginPath();
          ctx.arc(px, spritePy, r * climbScale, 0, Math.PI * 2);
          ctx.fill();
          // Trigger lazy-load
          if (!imageCache.current.has(spriteUrl)) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = spriteUrl;
            imageCache.current.set(spriteUrl, img);
          }
        }
        // HP bar above enemy
        const barW = r * 2;
        const barH = 4;
        const hpPct = e.hp / e.maxHp;
        ctx.fillStyle = 'rgba(40,0,0,0.6)';
        ctx.fillRect(px - barW / 2, py - r - 8, barW, barH);
        ctx.fillStyle = hpPct > 0.5 ? '#80ff80' : hpPct > 0.25 ? '#ffaa40' : '#ff4040';
        ctx.fillRect(px - barW / 2, py - r - 8, barW * hpPct, barH);
        // ── BETA 15.62 — JUMP! text static position, no float ──
        if (isPhasing) {
          const fade = phaseProgress < 0.85 ? 1 : (1 - (phaseProgress - 0.85) / 0.15);
          // Anchor to BASE size (not climb-scaled drawSize) so text doesn't bob
          const baseR = e.size * zoom;
          const textY = py - baseR - 18 * zoom;
          const fontSize = Math.max(20, 28 * zoom);
          ctx.save();
          ctx.globalAlpha = fade;
          ctx.font = `bold ${Math.round(fontSize)}px "Share Tech Mono", monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 6;
          ctx.strokeText('JUMP!', px, textY);
          ctx.fillStyle = '#ffe833';
          ctx.fillText('JUMP!', px, textY);
          ctx.restore();
        }
        // ── BETA 15.62 — Charging ring synced to 1.0s threshold ──
        if (e.wallPressTimer > 0.1 && !isPhasing) {
          const pct = Math.min(1, e.wallPressTimer / 1.0);
          const ringR = r + 6 + Math.sin(sim.simTime * 8) * 2;
          ctx.strokeStyle = `rgba(255,${Math.round(220 - pct * 200)},0,${0.4 + pct * 0.5})`;
          ctx.lineWidth = 2 + pct * 2;
          ctx.beginPath();
          ctx.arc(px, py, ringR, -Math.PI / 2, -Math.PI / 2 + pct * Math.PI * 2);
          ctx.stroke();
        }
      }

      // Bullets (small yellow dashes)
      ctx.strokeStyle = '#ffe040';
      ctx.lineWidth = 2;
      for (const b of sim.bullets) {
        const [bx, by] = w2c(b.x, b.y);
        const tailLen = 12 * zoom;
        const dx = b.vx, dy = b.vy;
        const d = Math.hypot(dx, dy) || 1;
        const tx = bx - (dx / d) * tailLen;
        const ty = by - (dy / d) * tailLen;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }

      // Bot — BETA 15.59 REAL SPRITE (cowboy_peanut)
      if (sim.bot) {
        const bot = sim.bot;
        const [bx, by] = w2c(bot.x, bot.y);
        const r = bot.size * zoom;
        const alive = bot.hp > 0;
        const drawSize = r * 2;
        const spriteUrl = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663404008214/cowboy_peanut_portrait_1bfb4f92.png';
        const botSprite = imageCache.current.get(spriteUrl);
        if (botSprite && botSprite.complete && botSprite.naturalWidth > 0) {
          const aspect = botSprite.naturalWidth / botSprite.naturalHeight;
          const sw = drawSize * aspect;
          const sh = drawSize;
          if (!alive) ctx.globalAlpha = 0.4;
          ctx.drawImage(botSprite, bx - sw / 2, by - sh / 2, sw, sh);
          ctx.globalAlpha = 1.0;
        } else {
          // Fallback to circle while sprite loads
          ctx.fillStyle = alive ? 'rgba(80,220,255,0.4)' : 'rgba(120,40,40,0.4)';
          ctx.strokeStyle = alive ? '#80f0ff' : '#ff4040';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(bx, by, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          if (!imageCache.current.has(spriteUrl)) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = spriteUrl;
            imageCache.current.set(spriteUrl, img);
          }
        }
        // HP bar
        const barW = r * 2;
        const barH = 5;
        const hpPct = Math.max(0, bot.hp / bot.maxHp);
        ctx.fillStyle = 'rgba(40,40,40,0.7)';
        ctx.fillRect(bx - barW / 2, by - r - 10, barW, barH);
        ctx.fillStyle = hpPct > 0.5 ? '#80ff80' : hpPct > 0.25 ? '#ffaa40' : '#ff4040';
        ctx.fillRect(bx - barW / 2, by - r - 10, barW * hpPct, barH);
        // Goal indicator (faint line to current goal)
        if (alive) {
          const [gx, gy] = w2c(bot.goalX, bot.goalY);
          ctx.strokeStyle = 'rgba(80,220,255,0.25)';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.lineTo(gx, gy);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }

    // === LAYER 8: Player hitbox + 5-corner check ===
    const result = checkCollision(playerWorld.x, playerWorld.y, PLAYER_SIZE, rooms);
    const [pcx, pcy] = w2c(playerWorld.x, playerWorld.y);
    const r_px = PLAYER_SIZE * zoom;
    ctx.fillStyle = result.walkable ? 'rgba(80,200,120,0.4)' : 'rgba(255,80,80,0.4)';
    ctx.strokeStyle = result.walkable ? '#80ff80' : '#ff5050';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(pcx, pcy, r_px, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    for (const c of result.corners) {
      const [cx, cy] = w2c(c.x, c.y);
      ctx.fillStyle = c.hits ? '#ff3030' : '#ffffff';
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Hover indicator
    if (hoverWorld) {
      const hRes = checkCollision(hoverWorld.x, hoverWorld.y, PLAYER_SIZE, rooms);
      const [hx, hy] = w2c(hoverWorld.x, hoverWorld.y);
      ctx.strokeStyle = hRes.walkable ? 'rgba(80,200,120,0.5)' : 'rgba(255,80,80,0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(hx, hy, PLAYER_SIZE * zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [shape, showZoneOverlay, showExteriorCurves, showRoomPng, showCorners, showDoorwayTrap, playerWorld, hoverWorld, rooms, exteriorCurves, layoutBounds, layoutW, layoutH, roomPngUrls, simFrame, showStuckHeatmap]);

  const eventToWorld = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (canvas.width / rect.width);
    const py = (e.clientY - rect.top) * (canvas.height / rect.height);
    const W = canvas.width;
    const H = canvas.height;
    const zoomX = (W - 80) / layoutW;
    const zoomY = (H - 80) / layoutH;
    const zoom = Math.min(zoomX, zoomY) * ZOOM_DEFAULT;
    const offsetX = (W - layoutW * zoom) / 2 - layoutBounds.minX * zoom;
    const offsetY = (H - layoutH * zoom) / 2 - layoutBounds.minY * zoom;
    return { x: (px - offsetX) / zoom, y: (py - offsetY) / zoom };
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => setPlayerWorld(eventToWorld(e));
  const handleMove = (e: React.MouseEvent<HTMLCanvasElement>) => setHoverWorld(eventToWorld(e));
  const handleLeave = () => setHoverWorld(null);

  const liveResult = checkCollision(playerWorld.x, playerWorld.y, PLAYER_SIZE, rooms);

  // Curve type counts for the sidebar
  const curveCounts = exteriorCurves.reduce<Record<string, number>>((acc, c) => {
    acc[c.type] = (acc[c.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: '#0a0c10',
      display: 'flex', flexDirection: 'row',
      fontFamily: "'Share Tech Mono', monospace",
      color: '#dde',
    }}>
      {/* Sidebar */}
      <div style={{
        width: 300, padding: 16,
        borderRight: '1px solid rgba(120,200,255,0.25)',
        overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#7fdcff', letterSpacing: 2 }}>
          🏛️ ROOM LAB
        </div>
        <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5 }}>
          Live geometry from <code>roomMapgen.ts</code> + <code>roomCollisionGrid.ts</code>.
          Click canvas to place player. Hover for live walkable check.
        </div>

        <label style={{ fontSize: 11, color: '#aaa' }}>
          LAYOUT (sorted simple → complex)
          <select
            value={shapeName}
            onChange={e => setShapeName(e.target.value)}
            style={{
              width: '100%', marginTop: 4, padding: '6px 8px',
              background: '#1a1f25', color: '#dde',
              border: '1px solid rgba(120,200,255,0.4)', borderRadius: 2,
              fontFamily: 'inherit', fontSize: 12,
            }}
          >
            {SORTED_SHAPES.map(s => (
              <option key={s.name} value={s.name}>
                {s.name} — {s.cells.length} room{s.cells.length === 1 ? '' : 's'} · weight {s.weight}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
          <label><input type="checkbox" checked={showRoomPng}        onChange={e => setShowRoomPng(e.target.checked)} /> Room PNGs (graffiti art)</label>
          <label><input type="checkbox" checked={showZoneOverlay}    onChange={e => setShowZoneOverlay(e.target.checked)} /> 3-zone collision overlay</label>
          <label><input type="checkbox" checked={showCorners}        onChange={e => setShowCorners(e.target.checked)} /> Corner clip triangles</label>
          <label><input type="checkbox" checked={showDoorwayTrap}    onChange={e => setShowDoorwayTrap(e.target.checked)} /> Doorway trapezoid outlines</label>
          <label><input type="checkbox" checked={showExteriorCurves} onChange={e => setShowExteriorCurves(e.target.checked)} /> Exterior green curves</label>
          <label><input type="checkbox" checked={showStuckHeatmap}   onChange={e => setShowStuckHeatmap(e.target.checked)} /> Stuck heatmap (sim)</label>
        </div>

        {/* === BETA 15.37 — Simulation controls === */}
        <div style={{
          padding: 10, borderRadius: 4,
          background: 'rgba(80,200,255,0.08)',
          border: '1px solid rgba(80,200,255,0.3)',
        }}>
          <div style={{ color: '#7fdcff', fontWeight: 700, fontSize: 12, marginBottom: 6, letterSpacing: 1 }}>
            🤖 COWBOY BOT SIM
          </div>
          <div style={{ fontSize: 10, color: '#888', lineHeight: 1.4, marginBottom: 8 }}>
            Cowboy stats, revolver, S{simStateRef.current.shrines.length}+C{simStateRef.current.chests.length} per layout. Auto-spawns 1 zombie/5s. Shrines/chests reset every 30s.
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={handleStartStop} style={btnStyle(simRunning ? '#ff8040' : '#80ff80')}>
              {simRunning ? '⏸ STOP' : '▶ START'}
            </button>
            <button onClick={handleResetSim} style={btnStyle('#aaa')}>↺ RESET</button>
            <button onClick={handleSpawnZombie} style={btnStyle('#ff5050')}>+ ZOMBIE</button>
            <button onClick={handleClearEnemies} style={btnStyle('#888')}>CLEAR</button>
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center', fontSize: 11 }}>
            SPEED:
            {[0.5, 1.0, 2.0, 4.0].map(s => (
              <button key={s} onClick={() => handleChangeSpeed(s)} style={{
                ...btnStyle(simSpeed === s ? '#7fdcff' : '#444'),
                fontSize: 10, padding: '3px 6px',
              }}>
                {s}×
              </button>
            ))}
          </div>
          {/* Live sim stats */}
          {(() => {
            const sim = simStateRef.current;
            const bot = sim.bot;
            const hpPct = bot ? Math.max(0, bot.hp / bot.maxHp) : 0;
            return (
              <div style={{ marginTop: 8, fontSize: 11, color: '#bbb', lineHeight: 1.5 }}>
                <div>Time: {sim.simTime.toFixed(1)}s</div>
                <div>Bot HP: <span style={{ color: hpPct > 0.5 ? '#80ff80' : hpPct > 0.25 ? '#ffaa40' : '#ff4040' }}>
                  {bot ? Math.max(0, Math.round(bot.hp)) : 0} / {bot?.maxHp ?? 0}
                </span></div>
                <div>Kills: {sim.totalKills}</div>
                <div>Shrines used: {sim.totalShrinesUsed}</div>
                <div>Chests opened: {sim.totalChestsOpened}</div>
                <div>Enemies alive: {sim.enemies.length}</div>
                <div style={{ color: '#ff8080' }}>Stuck events: {sim.stuckPoints.length}</div>
                {bot && bot.hp <= 0 && <div style={{ color: '#ff5050', fontWeight: 700 }}>💀 BOT DEAD — RESET TO RUN AGAIN</div>}
              </div>
            );
          })()}
        </div>

        {/* Live result */}
        <div style={{
          padding: 10, borderRadius: 4,
          background: liveResult.walkable ? 'rgba(80,200,120,0.15)' : 'rgba(255,80,80,0.15)',
          border: `1px solid ${liveResult.walkable ? '#5fc080' : '#ff5050'}`,
          fontSize: 13, fontWeight: 700,
          color: liveResult.walkable ? '#80ff90' : '#ff8080',
        }}>
          {liveResult.walkable ? '✅ WALKABLE' : '❌ BLOCKED'}
          <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4, fontWeight: 400 }}>
            Player ({Math.round(playerWorld.x)}, {Math.round(playerWorld.y)}) wu
          </div>
          <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, fontWeight: 400 }}>
            Corners hit: {liveResult.corners.filter(c => c.hits).length} / 5
          </div>
        </div>

        {/* Curve type breakdown */}
        <div style={{ fontSize: 10, color: '#888', lineHeight: 1.5 }}>
          <div style={{ color: '#aac', fontWeight: 700, marginBottom: 4 }}>EXTERIOR CURVES (this layout)</div>
          <div style={{ color: '#80ff80' }}>● convex: {curveCounts['convex'] ?? 0}</div>
          <div style={{ color: '#a8ff40' }}>● straight: {curveCounts['straight'] ?? 0}</div>
          <div style={{ color: '#ffd040' }}>● concave: {curveCounts['concave'] ?? 0}</div>
          <div style={{ color: '#40d0ff' }}>● diagonal_bridge: {curveCounts['diagonal_bridge'] ?? 0}</div>
          <div style={{ marginTop: 4, color: '#666' }}>Total: {exteriorCurves.length}</div>
        </div>

        {/* Geometry constants */}
        <div style={{ fontSize: 10, color: '#888', lineHeight: 1.5 }}>
          <div style={{ color: '#aac', fontWeight: 700, marginBottom: 4 }}>GEOMETRY</div>
          ROOM_SIZE: {ROOM_SIZE}wu<br />
          GAP_DEPTH (wall): {WALL}wu<br />
          DOORWAY (outer): 208wu wide ({N_OUT_L}–{N_OUT_R})<br />
          DOORWAY (inner): 168wu wide ({N_IN_L}–{N_IN_R})<br />
          CORNER CLIP: {CLIP}wu triangle<br />
          CROSS: {CROSS_LEFT}–{CROSS_RIGHT} × {CROSS_TOP}–{CROSS_BOTTOM}<br />
          PLAYER: {PLAYER_SIZE}wu radius<br />
          ROOMS placed at world ({LAB_ORIGIN_X}, {LAB_ORIGIN_Y}) — past EDGE_BUFFER, so no barricades.
        </div>

        <div style={{ flex: 1 }} />
        <button
          onClick={onBack}
          style={{
            padding: '10px', borderRadius: 2, cursor: 'pointer',
            background: 'rgba(80,200,255,0.15)',
            color: '#7fdcff',
            border: '1px solid rgba(120,200,255,0.5)',
            fontFamily: 'inherit', fontSize: 12, letterSpacing: 1,
          }}
        >
          ← BACK
        </button>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <canvas
          ref={canvasRef}
          width={1600}
          height={1000}
          onClick={handleClick}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          style={{ width: '100%', height: '100%', cursor: 'crosshair', display: 'block' }}
        />
      </div>
    </div>
  );
}
